import React from 'react'
import { css, cx } from '@emotion/css'
import { FormattedMessage } from 'react-intl'
import { DateRangePickerProps } from './date-range-picker'
import { DateRangeOption, DateRange, allDateRangeOptions } from './shared'
import { defaultSelectionToggleRenderer } from '../dropdown'
import Downshift, {
  GetToggleButtonPropsOptions,
  GetItemPropsOptions
} from 'downshift'
import { Popover, MenuOption, Menu } from '../popover'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { Placement } from '../popper'

export const i18nConfig: Record<DateRangeOption, string> = {
  [DateRangeOption.LAST_DAY]: 'detail_lastDay',
  [DateRangeOption.LAST_7_DAYS]: 'detail_lastSevenDays',
  [DateRangeOption.LAST_30_DAYS]: 'detail_lastThirtyDays',
  [DateRangeOption.LAST_12_MONTHS]: 'detail_lastTwelveMonths',
  [DateRangeOption.CUSTOM]: 'detail_customOption'
}

export interface ToggleRendererProps {
  readonly getToggleButtonProps: (options?: GetToggleButtonPropsOptions) => any
  readonly isOpen: boolean
  readonly selectedItem?: DateRangeOption
  readonly selectedDateRange?: DateRange
}

export type ToggleRenderer = (
  props: ToggleRendererProps
) => React.ReactElement<any>

interface Props {
  readonly renderToggle?: ToggleRenderer
  readonly selectedOption?: DateRangeOption
  readonly selectedDateRange?: DateRange
  readonly renderDateRangePicker: (
    props: DateRangePickerProps
  ) => React.ReactElement<any>
  readonly onDateRangeOptionSelect: (option: DateRangeOption) => void
  readonly onCustomDateRangeSelect: (option: DateRange) => void
  /* Optional list of date range options available in the popover */
  readonly dateRangeOptions?: DateRangeOption[]
  readonly placement?: Placement
  readonly renderAdditionalMenuContent?: () => React.ReactElement<any>
}

interface State {
  readonly isDropdownOpen: boolean
  readonly isCustomPickerOpen: boolean
}

export class DateRangePickerDropdown extends React.Component<Props, State> {
  public state: State = {
    isDropdownOpen: false,
    isCustomPickerOpen: false
  }

  public render() {
    const {
      selectedOption,
      selectedDateRange,
      renderDateRangePicker,
      dateRangeOptions,
      placement = 'bottom-end',
      renderAdditionalMenuContent
    } = this.props
    const { isDropdownOpen, isCustomPickerOpen } = this.state

    return (
      <Downshift
        selectedItem={selectedOption}
        onSelect={this.onDateRangeOptionSelect}
        isOpen={isCustomPickerOpen || isDropdownOpen}
        onOuterClick={this.onOuterClick}
      >
        {({ getToggleButtonProps, getItemProps, highlightedIndex }) => {
          const isOpen = isCustomPickerOpen || isDropdownOpen
          return (
            <div
              className={css`
                flex: 0 0 auto;
                width: auto;
                line-height: initial;
              `}
            >
              <Popover<HTMLDivElement>
                isOpen={isOpen}
                placement={isOpen ? 'bottom' : placement}
                renderTarget={({ getTargetProps }) => {
                  return (
                    <div {...getTargetProps()} onClick={this.onToggleClick}>
                      {this.renderToggle({
                        isOpen: isDropdownOpen,
                        getToggleButtonProps,
                        selectedItem: selectedOption,
                        selectedDateRange
                      })}
                    </div>
                  )
                }}
                renderContent={() => {
                  if (isDropdownOpen) {
                    return (
                      <Menu>
                        {renderAdditionalMenuContent &&
                          renderAdditionalMenuContent()}
                        {
                          <OptionsView
                            dateRangeOptions={
                              dateRangeOptions
                                ? dateRangeOptions
                                : allDateRangeOptions
                            }
                            selectedOption={selectedOption}
                            getItemProps={getItemProps}
                            highlightedIndex={highlightedIndex}
                          />
                        }
                      </Menu>
                    )
                  }

                  return renderDateRangePicker({
                    initialRange: selectedDateRange,
                    onApply: this.onDateRangePickerApply,
                    onCancel: this.onCloseDateRangePicker,
                    maxDate: 'NOW',
                    initialMonth: 'NOW'
                  })
                }}
              />
            </div>
          )
        }}
      </Downshift>
    )
  }

  private onDateRangeOptionSelect = (option: DateRangeOption) => {
    this.props.onDateRangeOptionSelect(option)
    this.setState({
      isCustomPickerOpen: option === DateRangeOption.CUSTOM,
      isDropdownOpen: false
    })
  }

  private onDateRangePickerApply = (dateRange: DateRange) => {
    this.props.onCustomDateRangeSelect(dateRange)
    this.setState({ isCustomPickerOpen: false })
  }

  private onToggleClick = () => {
    this.setState({ isDropdownOpen: true })
  }

  private onOuterClick = () => {
    this.setState({ isDropdownOpen: false, isCustomPickerOpen: false })
  }

  private onCloseDateRangePicker = () => {
    this.setState({ isCustomPickerOpen: false })
  }

  private renderToggle: ToggleRenderer = ({
    isOpen,
    getToggleButtonProps,
    ...rest
  }) => {
    const { renderToggle } = this.props
    const { isCustomPickerOpen } = this.state

    const renderToggleProps: ToggleRendererProps = {
      getToggleButtonProps: (options = {}) => {
        if (isCustomPickerOpen) {
          return {
            ...getToggleButtonProps(options),
            onClick: () => {
              if (isCustomPickerOpen) {
                this.onCloseDateRangePicker()
              }
            }
          }
        }
        return getToggleButtonProps(options)
      },
      isOpen: isOpen || isCustomPickerOpen,
      ...rest
    }

    return renderToggle
      ? renderToggle(renderToggleProps)
      : defaultSelectionToggleRenderer(renderToggleProps)
  }
}

function OptionsView({
  dateRangeOptions,
  selectedOption,
  getItemProps,
  highlightedIndex
}: {
  readonly dateRangeOptions: DateRangeOption[]
  readonly selectedOption?: DateRangeOption
  readonly getItemProps: (options: GetItemPropsOptions<any>) => any
  readonly highlightedIndex: number | null
}) {
  return (
    <>
      {dateRangeOptions.map((option, index) => (
        <FormattedMessage key={option} id={i18nConfig[option]}>
          {(text) => {
            return (
              <MenuOption
                {...getItemProps({
                  item: option,
                  className: cx(
                    {
                      [css`
                        background-color: ${theme.color.teal10};
                        transition: background-color 0.4s ease 0s;
                      `]:
                        highlightedIndex === index ||
                        (typeof highlightedIndex !== 'number' &&
                          option === selectedOption)
                    },
                    {
                      [css`
                        font-weight: ${deprecatedTheme.fontWeights.primary
                          .bold};
                      `]: option === selectedOption
                    }
                  )
                })}
              >
                {text}
              </MenuOption>
            )
          }}
        </FormattedMessage>
      ))}
    </>
  )
}
